<template>
    <div class="mt-6">
        <!-- Community Health + Brand Safety Sections -->
        <div v-if="!isDemographicsLoaded" class="mt-4">
            <div class="flex flex-col rounded border border-slate-100 px-10 py-16">
                <div class="text-center text-sm">
                    <p>Analyzing community health and brand safety...</p>
                </div>
                <div class="mt-3 h-2.5 w-full rounded bg-gray-200">
                    <div class="h-2.5 rounded bg-indigo-500 transition-all duration-1000" :style="{ width: `${demographicsLoadProgress}%` }"></div>
                </div>
            </div>
        </div>

        <div v-else-if="demographics" class="grid gap-4 md:grid-cols-2">
            <!-- Community Health Card -->
            <div v-if="demographics?.community_health" class="rounded-lg border border-slate-200 bg-white p-6">
                <div class="mb-4 flex items-center gap-2">
                    <AppIcon name="heart-pulse" class="h-5 w-5" />
                    <h2 class="text-lg font-semibold">Community Health</h2>
                </div>

                <!-- Excellent Community Health (A/B grades) -->
                <div v-if="getCommunityHealthStatus(demographics.community_health) === 'excellent'">
                    <div class="rounded-lg bg-emerald-50 p-4">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h3 class="font-semibold text-emerald-900">Outstanding Community for Brand Partnerships</h3>
                                <p class="mt-1 text-sm text-emerald-800">
                                    This creator has cultivated a healthy, engaged community that's ideal for brand sponsorships. The audience demonstrates strong positive engagement patterns with minimal toxic behavior, creating a safe and effective environment for brand messaging.
                                </p>
                            </div>
                            <svg class="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div v-if="demographics.community_health.cohesion_signals.length > 0" class="mt-3 border-t border-emerald-200 pt-3">
                            <p class="text-xs font-medium uppercase text-emerald-700">Key Strengths</p>
                            <ul class="mt-2 space-y-1.5">
                                <li
                                    v-for="signal in demographics.community_health.cohesion_signals.slice(0, 4)"
                                    :key="signal"
                                    class="flex items-start gap-2 text-sm text-emerald-800"
                                >
                                    <svg class="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>{{ signal }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Good Community Health (C grade but no D/F) -->
                <div v-else-if="getCommunityHealthStatus(demographics.community_health) === 'good'">
                    <div class="rounded-lg bg-amber-50 p-4">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h3 class="font-semibold text-amber-900">Healthy Community with Minor Considerations</h3>
                                <p class="mt-1 text-sm text-amber-800">
                                    This community shows generally positive engagement suitable for brand partnerships. While there are some areas that could be improved, the overall environment remains welcoming and brand-safe.
                                </p>
                            </div>
                            <svg class="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                        <div class="mt-4 space-y-3">
                            <div v-if="demographics.community_health.cohesion_signals.length > 0" class="border-t border-amber-200 pt-3">
                                <p class="text-xs font-medium uppercase text-amber-700">Strengths</p>
                                <ul class="mt-2 space-y-1.5">
                                    <li
                                        v-for="signal in demographics.community_health.cohesion_signals.slice(0, 3)"
                                        :key="signal"
                                        class="flex items-start gap-2 text-sm text-amber-800"
                                    >
                                        <svg class="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span>{{ signal }}</span>
                                    </li>
                                </ul>
                            </div>
                            <div v-if="demographics.community_health.toxicity_signals.length > 0" class="border-t border-amber-200 pt-3">
                                <p class="text-xs font-medium uppercase text-amber-700">Minor Considerations</p>
                                <ul class="mt-2 space-y-1.5">
                                    <li
                                        v-for="signal in demographics.community_health.toxicity_signals.slice(0, 2)"
                                        :key="signal"
                                        class="flex items-start gap-2 text-sm text-amber-800"
                                    >
                                        <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                        </svg>
                                        <span>{{ signal }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Concerning Community Health (D/F grades) -->
                <div v-else>
                    <div class="rounded-lg bg-rose-50 p-4">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h3 class="font-semibold text-rose-900">Concerning Patterns Detected</h3>
                                <p class="mt-1 text-sm text-rose-800">
                                    This community exhibits patterns that may pose risks for brand partnerships. Careful evaluation is recommended before proceeding with sponsorship opportunities.
                                </p>
                            </div>
                            <svg class="mt-0.5 h-6 w-6 flex-shrink-0 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>

                        <div class="mt-4 space-y-3">
                            <div v-if="demographics.community_health.toxicity_signals.length > 0" class="border-t border-rose-200 pt-3">
                                <p class="text-xs font-medium uppercase text-rose-700">Specific Concerns for Sponsors</p>
                                <ul class="mt-2 space-y-1.5">
                                    <li
                                        v-for="signal in demographics.community_health.toxicity_signals"
                                        :key="signal"
                                        class="flex items-start gap-2 text-sm text-rose-800"
                                    >
                                        <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                        </svg>
                                        <span>{{ signal }}</span>
                                    </li>
                                </ul>
                            </div>

                            <div v-if="demographics.community_health.cohesion_signals.length > 0" class="border-t border-rose-200 pt-3">
                                <p class="text-xs font-medium uppercase text-gray-600">Positive Aspects</p>
                                <ul class="mt-2 space-y-1.5">
                                    <li
                                        v-for="signal in demographics.community_health.cohesion_signals.slice(0, 4)"
                                        :key="signal"
                                        class="flex items-start gap-2 text-sm text-rose-800"
                                    >
                                        <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span>{{ signal }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Brand Safety Card -->
            <div v-if="demographics?.brand_safety" class="rounded-lg border border-slate-200 bg-white p-6">
                <div class="mb-4 flex items-center gap-2">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                    >
                        <path fill-rule="evenodd" d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08z" clip-rule="evenodd" />
                    </svg>
                    <h2 class="text-lg font-semibold">Brand Safety</h2>
                </div>

                <!-- Excellent Brand Safety (A/B risk level) -->
                <div v-if="getBrandSafetyStatus(demographics.brand_safety) === 'excellent'">
                    <div class="rounded-lg bg-emerald-50 p-4">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h3 class="font-semibold text-emerald-900">Clean Content Environment</h3>
                                <p class="mt-1 text-sm text-emerald-800">
                                    This creator's content maintains excellent brand safety standards. Their content is free from controversial topics and maintains a professional tone ideal for brand partnerships and sponsorships.
                                </p>
                            </div>
                            <svg class="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Good Brand Safety (C risk level) -->
                <div v-else-if="getBrandSafetyStatus(demographics.brand_safety) === 'good'">
                    <div class="rounded-lg bg-amber-50 p-4">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h3 class="font-semibold text-amber-900">Generally Safe with Minor Considerations</h3>
                                <p class="mt-1 text-sm text-amber-800">
                                    This creator's content is generally appropriate for brand partnerships. There are occasional topics that may require review, but overall the content maintains good brand safety standards.
                                </p>
                            </div>
                            <svg class="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>
                        <div v-if="demographics.brand_safety.risk_factors.some(f => ['C','D','F'].includes(getGrade(f.frequency, true)))" class="mt-4 border-t border-amber-200 pt-3">
                            <p class="text-xs font-medium uppercase text-amber-700">Occasional Content Themes</p>
                            <ul class="mt-2 space-y-1.5">
                                <li
                                    v-for="factor in _.orderBy(demographics.brand_safety.risk_factors, ['frequency'], ['desc']).filter(f => ['C','D','F'].includes(getGrade(f.frequency, true))).slice(0, 3)"
                                    :key="factor.factor"
                                    class="flex items-start gap-2 text-sm text-amber-800"
                                >
                                    <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                    <span>{{ factor.factor }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Concerning Brand Safety (D/F risk level) -->
                <div v-else>
                    <div class="rounded-lg bg-rose-50 p-4">
                        <div class="flex items-start justify-between gap-3">
                            <div>
                                <h3 class="font-semibold text-rose-900">Content Risks Detected</h3>
                                <p class="mt-1 text-sm text-rose-800">
                                    This creator's content frequently covers topics that may not align with brand safety standards. Careful review is recommended for potential sponsorships.
                                </p>
                            </div>
                            <svg class="mt-0.5 h-6 w-6 flex-shrink-0 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                            </svg>
                        </div>

                        <div v-if="demographics.brand_safety.risk_factors.some(f => ['C','D','F'].includes(getGrade(f.frequency, true)))" class="mt-4 border-t border-rose-200 pt-3">
                            <p class="text-xs font-medium uppercase text-rose-700">Content Risk Factors for Sponsors</p>
                            <ul class="mt-2 space-y-1.5">
                                <li
                                    v-for="factor in _.orderBy(demographics.brand_safety.risk_factors, ['frequency'], ['desc']).filter(f => ['C','D','F'].includes(getGrade(f.frequency, true)))"
                                    :key="factor.factor"
                                    class="flex items-start gap-2 text-sm text-rose-800"
                                >
                                    <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                    </svg>
                                    <span>{{ factor.factor }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Divider — only shown when brand safety/community health cards are visible -->
        <Separator v-if="demographics && (demographics.community_health || demographics.brand_safety)" class="my-8" />

        <!-- Background Check -->
        <div>
            <div class="mb-4 flex items-end justify-between">
                <div>
                    <h2 class="font-semibold text-slate-800">Background Check</h2>
                    <p class="text-sm text-slate-500">Negative press, controversies, and reputation signals</p>
                </div>
                <button
                    v-if="(newsHasFetched || isNewsLoading) && newsYearsList.length === 0"
                    class="flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="isNewsLoading"
                    @click="fetchNews(true)"
                >
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>

            <!-- Fan account gate -->
            <div v-if="isFanAccount" class="rounded-lg border border-amber-200 bg-amber-50 px-6 py-8 text-center">
                <p class="text-2xl">👥</p>
                <p class="mt-2 font-semibold text-amber-800">This is a fan account</p>
                <p class="mt-1 text-sm text-amber-700">This account is not run by the actual creator. Background check results are not available for fan accounts.</p>
            </div>

            <!-- Biography still loading — account type not confirmed yet -->
            <div v-else-if="biographyLoading && !props.creatorType" class="rounded-lg border border-slate-100 px-6 py-8 text-center">
                <p class="text-sm text-slate-400">Analyzing account type...</p>
            </div>

            <!-- Normal content -->
            <template v-else>

            <!-- Year tabs + refresh button row — only shown when there are year tabs -->
            <div v-if="newsYearsList.length > 0" class="mb-6 flex items-end justify-between border-b border-slate-200">
                <nav class="-mb-px flex">
                    <button
                        class="flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
                        :class="newsSelectedYear === null
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'"
                        @click="newsSelectedYear = null"
                    >
                        All
                    </button>
                    <button
                        v-for="year in newsYearsList"
                        :key="year"
                        class="flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
                        :class="yearTabClass(year)"
                        :disabled="isYearDisabled(year)"
                        @click="!isYearDisabled(year) && (newsSelectedYear = year)"
                    >
                        {{ year }}
                        <svg v-if="isNewsLoading && newsCurrentLoadingYear === year" class="h-3 w-3 animate-spin text-indigo-400" viewBox="0 0 24 24" fill="none">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                        <svg v-else-if="newsYearsDone.includes(year) && !isYearDisabled(year)" class="h-3 w-3 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                    </button>
                </nav>
                <button
                    v-if="newsYearsList.length > 0"
                    class="mb-1.5 flex items-center gap-1.5 rounded px-2 py-1 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    :disabled="isNewsLoading"
                    @click="fetchNews(true)"
                >
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>

            <!-- Incremental progress bar -->
            <div v-if="isNewsLoading && newsSearchMode === 'incremental'" class="mb-4">
                <p class="mb-1.5 text-xs text-slate-400">
                    <span v-if="newsIncrementalSince">Checking for updates since {{ formatArticleDate(newsIncrementalSince) }}...</span>
                    <span v-else>Checking for recent updates...</span>
                </p>
                <div class="h-1.5 w-full rounded bg-gray-200">
                    <div class="h-1.5 rounded bg-indigo-500 transition-all duration-500" :style="{ width: `${newsLoadProgress}%` }"></div>
                </div>
            </div>

            <!-- Error state -->
            <div v-if="newsError" class="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                <p class="font-medium text-red-700">Failed to load news</p>
                <p class="mt-1 text-sm text-red-500">{{ newsError }}</p>
                <button class="mt-3 text-sm font-medium text-red-600 underline hover:text-red-800" @click="fetchNews">Try again</button>
            </div>

            <template v-else>
                <!-- Skeleton — shown while loading and no results yet for the current view -->
                <div v-if="isNewsLoading && filteredControversies.length === 0" class="relative">
                    <div class="absolute bottom-0 top-0 w-px bg-slate-200" style="left: 116px;"></div>
                    <div class="space-y-6">
                        <div v-for="i in 2" :key="i" class="flex animate-pulse items-start">
                            <div class="mt-3 flex w-[108px] flex-shrink-0 items-center justify-end pr-2">
                                <div class="h-3 w-16 rounded bg-slate-200"></div>
                            </div>
                            <div class="relative z-10 mt-3 h-4 w-4 flex-shrink-0 rounded-full bg-slate-200"></div>
                            <div class="min-w-0 flex-1 pl-4">
                                <div class="rounded-lg border border-slate-200 bg-white">
                                    <div class="border-b border-slate-100 px-5 py-4">
                                        <div class="flex items-start justify-between gap-3">
                                            <div class="h-4 w-2/3 rounded bg-slate-200"></div>
                                            <div class="h-5 w-16 rounded-full bg-slate-200"></div>
                                        </div>
                                        <div class="mt-2 h-3 w-1/2 rounded bg-slate-100"></div>
                                    </div>
                                    <div class="flex gap-4 p-4">
                                        <div class="h-16 w-16 flex-shrink-0 rounded-lg bg-slate-200"></div>
                                        <div class="flex-1 space-y-2 pt-1">
                                            <div class="h-3 w-full rounded bg-slate-200"></div>
                                            <div class="h-3 w-4/5 rounded bg-slate-200"></div>
                                            <div class="h-3 w-24 rounded bg-slate-100"></div>
                                        </div>
                                    </div>
                                    <div class="flex gap-4 border-t border-slate-100 p-4">
                                        <div class="h-16 w-16 flex-shrink-0 rounded-lg bg-slate-200"></div>
                                        <div class="flex-1 space-y-2 pt-1">
                                            <div class="h-3 w-full rounded bg-slate-200"></div>
                                            <div class="h-3 w-3/5 rounded bg-slate-200"></div>
                                            <div class="h-3 w-20 rounded bg-slate-100"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty state -->
                <div v-else-if="!isNewsLoading && filteredControversies.length === 0" class="rounded-lg border border-slate-200 py-12 text-center">
                    <p class="font-medium text-slate-700">{{ emptyMessage }}</p>
                </div>

                <!-- Controversy list — timeline -->
                <div v-if="filteredControversies.length > 0" class="relative">
                    <div class="absolute bottom-0 top-0 w-px bg-slate-200" style="left: 116px;"></div>
                    <div class="space-y-6">
                        <div
                            v-for="(controversy, cIndex) in filteredControversies"
                            :key="cIndex"
                            class="flex items-start"
                        >
                            <div class="flex h-4 w-[108px] flex-shrink-0 items-center justify-end mt-3 pr-2">
                                <span class="whitespace-nowrap text-xs leading-none text-slate-400">{{ controversyDate(controversy) }}</span>
                            </div>
                            <div
                                class="relative z-10 mt-3 h-4 w-4 flex-shrink-0 rounded-full border-2 border-white"
                                :class="{
                                    'bg-rose-400 ring-1 ring-rose-200':      getControversySeverity(controversy) === 'high',
                                    'bg-orange-400 ring-1 ring-orange-200':  getControversySeverity(controversy) === 'medium',
                                    'bg-yellow-300 ring-1 ring-yellow-200':  getControversySeverity(controversy) === 'low',
                                    'bg-slate-300 ring-1 ring-slate-200':    !getControversySeverity(controversy),
                                }"
                            ></div>
                            <div class="min-w-0 flex-1 pl-4">
                                <div class="rounded-lg border border-slate-200 bg-white">
                                    <div class="border-b border-slate-100 px-5 py-4">
                                        <div class="flex items-start justify-between gap-3">
                                            <h3 class="font-semibold text-slate-800">{{ controversy.controversy_title }}</h3>
                                            <span
                                                v-if="getControversySeverity(controversy)"
                                                class="inline-flex flex-shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize"
                                                :class="{
                                                    'border-red-200 bg-red-100 text-red-700':          getControversySeverity(controversy) === 'high',
                                                    'border-orange-200 bg-orange-100 text-orange-700': getControversySeverity(controversy) === 'medium',
                                                    'border-yellow-200 bg-yellow-100 text-yellow-700': getControversySeverity(controversy) === 'low',
                                                }"
                                            >
                                                {{ getControversySeverity(controversy) }}
                                            </span>
                                        </div>
                                        <p v-if="controversy.description" class="mt-1 text-sm text-slate-500">{{ controversy.description }}</p>
                                    </div>
                                    <div class="divide-y divide-slate-100">
                                        <div
                                            v-for="article in controversy.articles"
                                            :key="article.url"
                                            class="flex gap-4 p-4 transition-shadow hover:shadow-sm"
                                        >
                                            <div class="h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-slate-100">
                                                <img
                                                    v-if="!article._imgFailed && (article.thumbnail_url || article.logo_url)"
                                                    :src="article.thumbnail_url || article.logo_url"
                                                    :alt="article.source || ''"
                                                    class="h-full w-full"
                                                    :class="article.thumbnail_url ? 'object-cover' : 'object-contain p-2'"
                                                    @error="article._imgFailed = true"
                                                />
                                                <div
                                                    v-else
                                                    class="flex h-full w-full items-center justify-center"
                                                    :style="{ backgroundColor: sourceColor(article.source) }"
                                                >
                                                    <span class="text-lg font-semibold text-white">{{ sourceInitial(article.source) }}</span>
                                                </div>
                                            </div>
                                            <div class="min-w-0 flex-1">
                                                <div class="mb-1">
                                                    <a
                                                        :href="article.url"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        class="font-medium text-slate-900 hover:text-indigo-600 hover:underline"
                                                    >
                                                        {{ article.title }}
                                                    </a>
                                                </div>
                                                <p class="mb-2 text-xs text-slate-400">
                                                    <span v-if="article.source">{{ cleanSource(article.source) }}</span>
                                                    <span v-if="article.source && article.published_at"> &middot; </span>
                                                    <span v-if="article.published_at">{{ formatArticleDate(article.published_at) }}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            </template> <!-- end v-else normal content -->
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { axios } from '@/utils/axios'
import _ from 'lodash'
import { Separator } from '@/components/ui/separator'

const store = useStore()

const props = defineProps({
    creatorId: {
        type: String,
        default: null,
    },
    creatorName: {
        type: String,
        default: 'this creator',
    },
    platformId: {
        type: String,
        required: true,
    },
    platformUserId: {
        type: String,
        required: true,
    },
    creatorType: {
        type: String,
        default: null,
    },
    biographyChannelType: {
        type: String,
        default: null,
    },
    biographyLoading: {
        type: Boolean,
        default: false,
    },
    username: {
        type: String,
        default: null,
    },
    description: {
        type: String,
        default: null,
    },
    country: {
        type: String,
        default: null,
    },
})

const isFanAccount = computed(() =>
    props.creatorType === 'fan_account' || props.biographyChannelType === 'fan_account'
)

// --- Demographics (community health + brand safety) ---
const isDemographicsLoaded = ref(false)
const demographics = ref(null)
const demographicsLoadProgress = ref(0)
let demographicsProgressInterval = null

const startDemographicsProgress = () => {
    demographicsLoadProgress.value = 0
    demographicsProgressInterval = setInterval(() => {
        demographicsLoadProgress.value = Math.min(demographicsLoadProgress.value + 100 / 90, 100)
        if (demographicsLoadProgress.value >= 100) clearInterval(demographicsProgressInterval)
    }, 1000)
}

const stopDemographicsProgress = () => {
    clearInterval(demographicsProgressInterval)
    demographicsLoadProgress.value = 100
}

const getGrade = (score, isNegative = false) => {
    const grades = isNegative ? { A: 0.2, B: 0.4, C: 0.6, D: 0.8, F: 1 } : { F: 0.2, D: 0.4, C: 0.6, B: 0.8, A: 1 }
    for (const [grade, threshold] of Object.entries(grades)) {
        if (score <= threshold) return grade
    }
    return 'F'
}

const getCommunityHealthStatus = (communityHealth) => {
    if (!communityHealth) return null
    const cohesionGrade = getGrade(communityHealth.cohesion_score)
    const toxicityGrade = getGrade(communityHealth.toxicity_score, true)
    if (['A', 'B'].includes(cohesionGrade) && ['A', 'B'].includes(toxicityGrade)) return 'excellent'
    if (cohesionGrade === 'C' || toxicityGrade === 'C') {
        if (!['D', 'F'].includes(cohesionGrade) && !['D', 'F'].includes(toxicityGrade)) return 'good'
    }
    return 'concern'
}

const getBrandSafetyStatus = (brandSafety) => {
    if (!brandSafety) return null
    const riskGrade = getGrade(brandSafety.overall_risk_level, true)
    if (['A', 'B'].includes(riskGrade)) return 'excellent'
    if (riskGrade === 'C') return 'good'
    return 'concern'
}

const fetchDemographics = async () => {
    isDemographicsLoaded.value = false
    startDemographicsProgress()
    try {
        const { data } = await axios.get(`/platforms/${props.platformId}/${props.platformUserId}/analysis/audience`)
        demographics.value = data.payload.analysis || false
    } catch (err) {
        console.error('Error fetching demographics for safety tab', err)
        demographics.value = false
    } finally {
        stopDemographicsProgress()
        isDemographicsLoaded.value = true
    }
}

// --- News (background check) ---
const years = 5
const isNewsLoading = ref(false)
const controversies = ref([])
const emptyMessage = ref('')
const newsError = ref(null)
const newsLoadProgress = ref(0)
const newsScanYears = ref([])   // years the backend is actively scanning (for spinner state)
const newsYearsDone = ref([])
const newsSelectedYear = ref(null)
const newsSearchMode = ref(null)
const newsIncrementalSince = ref(null)
const newsHasFetched = ref(false)
let newsEventSource = null
let newsIncrementalProgressInterval = null

const newsYearsList = computed(() => {
    const allYears = [...new Set([
        ...newsScanYears.value,
        ...controversies.value.map(c => {
            const raw = controversyDateStr(c)
            return raw ? new Date(raw).getFullYear() : null
        }).filter(Boolean)
    ])].sort((a, b) => b - a)

    // While loading, only reveal years up to and including the one currently being scanned
    if (isNewsLoading.value) {
        const currentYear = newsScanYears.value.find(y => !newsYearsDone.value.includes(y))
        if (currentYear) {
            const idx = allYears.indexOf(currentYear)
            return idx !== -1 ? allYears.slice(0, idx + 1) : allYears
        }
    }

    return allYears
})

const newsCurrentLoadingYear = computed(() => {
    if (!isNewsLoading.value) return null
    return newsScanYears.value.find(y => !newsYearsDone.value.includes(y)) ?? null
})

const controversiesForYear = (year) =>
    sortedControversies.value.filter(c => {
        const raw = controversyDateStr(c)
        return raw ? new Date(raw).getFullYear() === year : false
    })

const isYearDisabled = (year) =>
    newsYearsDone.value.includes(year) && controversiesForYear(year).length === 0

const yearTabClass = (year) => {
    if (isYearDisabled(year)) return 'border-transparent text-slate-300 cursor-not-allowed'
    return newsSelectedYear.value === year
        ? 'border-indigo-500 text-indigo-600'
        : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
}

const filteredControversies = computed(() => {
    if (!newsSelectedYear.value) return sortedControversies.value
    return controversiesForYear(newsSelectedYear.value)
})

const AVATAR_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#64748b']
const cleanSource = (source) => (source || '').replace(/\d{4}-\d{2}-\d{2}T[\d:.Z]+$/, '').trim()
const formatArticleDate = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (isNaN(d)) return dateStr
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
const sourceColor = (source) => AVATAR_COLORS[(cleanSource(source) || '?').charCodeAt(0) % AVATAR_COLORS.length]
const sourceInitial = (source) => (cleanSource(source) || '?').charAt(0).toUpperCase()

const controversyDateStr = (controversy) => {
    if (controversy.date && !isNaN(new Date(controversy.date))) return controversy.date
    const dates = (controversy.articles || []).map(x => x.published_at).filter(Boolean).filter(d => !isNaN(new Date(d)))
    return dates.length ? [...dates].sort()[0] : ''
}

const sortedControversies = computed(() =>
    [...controversies.value].sort((a, b) =>
        controversyDateStr(b).localeCompare(controversyDateStr(a))
    )
)

const getControversySeverity = (controversy) => {
    const severityOrder = ['high', 'medium', 'low']
    const severities = (controversy.articles || []).map(a => a.severity).filter(s => severityOrder.includes(s))
    if (!severities.length) return null
    const counts = { high: 0, medium: 0, low: 0 }
    for (const s of severities) counts[s]++
    const maxCount = Math.max(counts.high, counts.medium, counts.low)
    return severityOrder.find(s => counts[s] === maxCount)
}

const controversyDate = (controversy) => {
    const raw = controversyDateStr(controversy)
    if (!raw) return null
    try {
        const d = new Date(raw)
        if (!isNaN(d)) return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } catch {}
    return null
}

const fetchNews = async (refresh = false) => {
    if (newsEventSource) { newsEventSource.abort(); newsEventSource = null }

    isNewsLoading.value = true
    newsError.value = null
    newsLoadProgress.value = 0
    newsScanYears.value = []
    newsYearsDone.value = []
    newsSelectedYear.value = null
    newsSearchMode.value = null
    newsIncrementalSince.value = null
    controversies.value = []

    const rawParams = { years, name: props.creatorName || 'this creator', username: props.username, description: props.description, country: props.country, ...(refresh ? { refresh: 'true' } : {}) }
    const params = new URLSearchParams(Object.fromEntries(Object.entries(rawParams).filter(([, v]) => v != null)))
    const url = `${import.meta.env.VITE_APP_ROOT_API}/creators/${props.platformId}:${props.platformUserId}/news?${params}`

    const headers = {
        Authorization: `Bearer ${store.state.authentication.tokens.core}`,
    }
    const orgId = store.getters.selfActiveOrganization?.id
    if (orgId) headers['x-active-organization-id'] = orgId

    const controller = new AbortController()
    newsEventSource = controller

    let buffer = ''
    try {
        const response = await fetch(url, { headers, signal: controller.signal })
        if (!response.ok) {
            newsError.value = `Request failed (${response.status})`
            isNewsLoading.value = false
            return
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        // Keep event state outside the read loop so events spanning multiple chunks aren't lost
        let eventType = null
        let dataLine = null

        while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() // keep incomplete last line

            for (const line of lines) {
                if (line.startsWith('event:')) {
                    eventType = line.slice(6).trim()
                } else if (line.startsWith('data:')) {
                    dataLine = line.slice(5).trim()
                } else if (line === '' && eventType && dataLine) {
                    try {
                        const payload = JSON.parse(dataLine)
                        if (eventType === 'start') {
                            newsSearchMode.value = payload.search_mode || null
                            if (payload.search_mode === 'incremental') {
                                newsIncrementalSince.value = payload.since_date || null
                                if (payload.existing_controversies?.length) {
                                    controversies.value = payload.existing_controversies
                                }
                                newsScanYears.value = payload.years || []
                                newsIncrementalProgressInterval = setInterval(() => {
                                    newsLoadProgress.value = Math.min(newsLoadProgress.value + 1, 90)
                                }, 300)
                            } else {
                                newsScanYears.value = payload.years || []
                            }
                        } else if (eventType === 'year') {
                            const doneYear = Number(payload.year)
                            newsYearsDone.value = [...newsYearsDone.value, doneYear]
                            for (const controversy of (payload.controversies || [])) {
                                controversies.value.push(controversy)
                            }
                        } else if (eventType === 'done') {
                            clearInterval(newsIncrementalProgressInterval)
                            if (payload.controversies) controversies.value = payload.controversies
                            emptyMessage.value = payload.empty_message || `No negative press found for ${props.creatorName} in this period.`
                            newsLoadProgress.value = 100
                            isNewsLoading.value = false
                        } else if (eventType === 'error') {
                            clearInterval(newsIncrementalProgressInterval)
                            newsError.value = payload.error || 'Something went wrong'
                            isNewsLoading.value = false
                        }
                    } catch {}
                    eventType = null
                    dataLine = null
                }
            }
        }
    } catch (err) {
        if (err.name !== 'AbortError') {
            newsError.value = err.message || 'Something went wrong'
            isNewsLoading.value = false
        }
    } finally {
        if (newsEventSource === controller) newsEventSource = null
        isNewsLoading.value = false
        newsHasFetched.value = true
    }
}

onMounted(() => {
    fetchDemographics()
    fetchNews()
})

onUnmounted(() => {
    clearInterval(demographicsProgressInterval)
    clearInterval(newsIncrementalProgressInterval)
    if (newsEventSource) newsEventSource.abort()
})
</script>
